import {
  ProjectsListViewSchema,
  type ProjectSummary,
  type ProjectsListSkippedProject,
  type ProjectsListView
} from "@automatry/schemas";

export type ProjectListSortKey = "recent" | "created" | "name" | "status";

export type ProjectDashboardSortKey =
  | ProjectListSortKey
  | "attention"
  | "openDecisions"
  | "blockingRisks"
  | "readiness"
  | "apartments";

export type ProjectDashboardFilterKey = "all" | "attention" | "blocked" | "ready";

export interface ProjectsSummaryFixture {
  fixture: {
    id: string;
    generatedFrom: string;
    sourceInputs: string[];
    pipelineInputMode: string;
    generatedAt: string;
  };
  projectIds: string[];
  status: "loading" | "ready" | "blocked" | "error";
  updatedAt: string;
  projects: ProjectSummary[];
  skippedProjects: ProjectsListSkippedProject[];
  generatedFrom?: string;
}

export interface ProjectDashboardCard extends ProjectSummary {
  routeKind: "fullWorkspace" | "summaryOnly";
  readinessLabel: string;
  attentionScore: number;
  attentionReasons: string[];
  supplyMixLabel: string;
  edgeIncluded: boolean;
  edgeDefaultApplied: boolean;
  edgeInclusionWhy: string;
  edgeInclusionWhere: string;
  edgeCapabilities: string[];
}

export interface ProjectsDashboardViewModel extends ProjectsListView {
  title: string;
  subtitle: string;
  cards: ProjectDashboardCard[];
  visibleCards: ProjectDashboardCard[];
  sortKey: ProjectDashboardSortKey;
  filterKey: ProjectDashboardFilterKey;
  searchQuery: string;
  totals: {
    projectCount: number;
    apartmentCount: number;
    apartmentTypeCount: number;
    openDecisionCount: number;
    needsUserDecisionCount: number;
    blockingRiskCount: number;
    anomalyCount: number;
    readyCount: number;
    blockedCount: number;
    attentionCount: number;
    controllerCount: number;
    deviceCount: number;
    infraCount: number;
    bomRowCount: number;
    bomUnitCount: number;
  };
  dataGaps: string[];
}

const noTriggerDefaultLabel =
  "Default - no specific requirement found in the documents";

const readinessRank = (readiness: ProjectSummary["exportReadiness"]): number => {
  if (readiness === "blocked") {
    return 2;
  }

  if (readiness === "not_ready") {
    return 1;
  }

  return 0;
};

const readinessLabel = (readiness: ProjectSummary["exportReadiness"]): string => {
  if (readiness === "not_ready") {
    return "Not ready";
  }

  return readiness === "ready" ? "Ready" : "Blocked";
};

const attentionScore = (project: ProjectSummary): number =>
  project.blockingRiskCount * 1000 +
  project.needsUserDecisionCount * 10 +
  project.clientClarificationCount +
  project.anomalyCount;

const attentionReasons = (project: ProjectSummary): string[] => {
  const reasons: string[] = [];

  if (project.blockingRiskCount > 0) {
    reasons.push(`${project.blockingRiskCount} blocking risk`);
  }

  if (project.needsUserDecisionCount > 0) {
    reasons.push(`${project.needsUserDecisionCount} needs your decision`);
  }

  if (project.clientClarificationCount > 0) {
    reasons.push(`${project.clientClarificationCount} clarifications`);
  }

  if (project.anomalyCount > 0) {
    reasons.push(`${project.anomalyCount} items to check`);
  }

  return reasons.length > 0 ? reasons : ["No attention items"];
};

const supplyMixLabel = (project: ProjectSummary): string => {
  const rollup = project.supplySourceRollup;
  const parts = [
    rollup.titan > 0 ? `${rollup.titan} Titan units` : undefined,
    rollup.fabricated > 0 ? `${rollup.fabricated} fabricated units` : undefined,
    rollup.thirdParty > 0 ? `${rollup.thirdParty} third-party units` : undefined,
    rollup.inHouse > 0 ? `${rollup.inHouse} in-house units` : undefined
  ].filter((part): part is string => part !== undefined);

  return parts.length > 0 ? parts.join(" · ") : "No BOM units";
};

const edgeCapabilities = [
  "Edge gateway allowance",
  "BACnet/IP dashboard path",
  "Alert-ready workflow"
];

const toCard = (project: ProjectSummary): ProjectDashboardCard => ({
  ...project,
  routeKind: project.id === "carrington" ? "fullWorkspace" : "summaryOnly",
  readinessLabel: readinessLabel(project.exportReadiness),
  attentionScore: attentionScore(project),
  attentionReasons: attentionReasons(project),
  supplyMixLabel: supplyMixLabel(project),
  edgeIncluded: true,
  edgeDefaultApplied: !project.edgePresent,
  edgeInclusionWhy: project.edgePresent
    ? "AI-produced Edge output is attached to this project."
    : `${noTriggerDefaultLabel}. Edge is included for every controls project unless removed during review.`,
  edgeInclusionWhere: project.edgePresent
    ? "Where: project summary reports Edge output is present."
    : "Where: no project-specific Edge trigger is attached to this summary card.",
  edgeCapabilities
});

const compareProjectName = (
  left: Pick<ProjectSummary, "name">,
  right: Pick<ProjectSummary, "name">
): number => left.name.localeCompare(right.name);

const projectTimestamp = (value: string | undefined): number => {
  if (!value) {
    return 0;
  }

  const timestamp = Date.parse(value);

  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const projectStatusRank: Record<NonNullable<ProjectSummary["status"]>, number> = {
  failed: 0,
  partial: 1,
  specifying: 2,
  requirements: 3,
  inputs: 4,
  draft: 5,
  specified: 6
};

export const projectStatusLabel = (status: ProjectSummary["status"]): string => {
  if (!status) {
    return "Saved";
  }

  return status.slice(0, 1).toUpperCase() + status.slice(1);
};

export const projectMatchesSearch = (
  project: Pick<ProjectSummary, "name" | "client" | "location" | "siteAddress">,
  query: string
): boolean => {
  const normalizedQuery = query.trim().toLocaleLowerCase();

  if (normalizedQuery.length === 0) {
    return true;
  }

  return [project.name, project.client, project.location, project.siteAddress].some(
    (value) => value?.toLocaleLowerCase().includes(normalizedQuery) ?? false
  );
};

export const sortProjectList = <
  Project extends Pick<ProjectSummary, "name" | "status" | "createdAt" | "updatedAt">
>(
  projects: readonly Project[],
  sortKey: ProjectListSortKey
): Project[] =>
  [...projects].sort((left, right) => {
    if (sortKey === "recent") {
      return (
        projectTimestamp(right.updatedAt) - projectTimestamp(left.updatedAt) ||
        compareProjectName(left, right)
      );
    }

    if (sortKey === "created") {
      return (
        projectTimestamp(right.createdAt) - projectTimestamp(left.createdAt) ||
        compareProjectName(left, right)
      );
    }

    if (sortKey === "status") {
      return (
        (projectStatusRank[left.status ?? "draft"] ?? Number.MAX_SAFE_INTEGER) -
          (projectStatusRank[right.status ?? "draft"] ?? Number.MAX_SAFE_INTEGER) ||
        compareProjectName(left, right)
      );
    }

    return compareProjectName(left, right);
  });

export const sortProjectCards = (
  cards: readonly ProjectDashboardCard[],
  sortKey: ProjectDashboardSortKey
): ProjectDashboardCard[] => {
  if (["recent", "created", "name", "status"].includes(sortKey)) {
    return sortProjectList(cards, sortKey as ProjectListSortKey);
  }

  return [...cards].sort((left, right) => {
    if (sortKey === "openDecisions") {
      return (
        right.openDecisionCount - left.openDecisionCount ||
        compareProjectName(left, right)
      );
    }

    if (sortKey === "blockingRisks") {
      return (
        right.blockingRiskCount - left.blockingRiskCount ||
        compareProjectName(left, right)
      );
    }

    if (sortKey === "readiness") {
      return (
        readinessRank(right.exportReadiness) - readinessRank(left.exportReadiness) ||
        compareProjectName(left, right)
      );
    }

    if (sortKey === "apartments") {
      return (
        right.apartmentCount - left.apartmentCount || compareProjectName(left, right)
      );
    }

    return (
      right.attentionScore - left.attentionScore || compareProjectName(left, right)
    );
  });
};

const filterCards = (
  cards: readonly ProjectDashboardCard[],
  filterKey: ProjectDashboardFilterKey
): ProjectDashboardCard[] => {
  if (filterKey === "attention") {
    return cards.filter((project) => project.attentionScore > 0);
  }

  if (filterKey === "blocked") {
    return cards.filter((project) => project.exportReadiness === "blocked");
  }

  if (filterKey === "ready") {
    return cards.filter((project) => project.exportReadiness === "ready");
  }

  return [...cards];
};

export const projectsSummaryFixtureToDashboardView = (
  fixture: ProjectsSummaryFixture,
  options: {
    sortKey?: ProjectDashboardSortKey;
    filterKey?: ProjectDashboardFilterKey;
    searchQuery?: string;
  } = {}
): ProjectsDashboardViewModel => {
  const sortKey = options.sortKey ?? "attention";
  const filterKey = options.filterKey ?? "all";
  const searchQuery = options.searchQuery ?? "";
  const core = ProjectsListViewSchema.parse({
    projectIds: fixture.projectIds,
    status: fixture.status,
    updatedAt: fixture.updatedAt,
    projects: fixture.projects,
    skippedProjects: fixture.skippedProjects,
    generatedFrom: fixture.generatedFrom
  });
  const cards = sortProjectCards(core.projects.map(toCard), sortKey);
  const visibleCards = filterCards(cards, filterKey).filter((project) =>
    projectMatchesSearch(project, searchQuery)
  );

  return {
    ...core,
    title: "Projects",
    subtitle:
      core.projects.length === 0
        ? "Create your first controls specification from uploaded project documents."
        : "Which projects are ready, which need a decision, and what should you open next?",
    cards,
    visibleCards,
    sortKey,
    filterKey,
    searchQuery,
    totals: {
      projectCount: core.projects.length,
      apartmentCount: core.projects.reduce(
        (sum, project) => sum + project.apartmentCount,
        0
      ),
      apartmentTypeCount: core.projects.reduce(
        (sum, project) => sum + project.apartmentTypeCount,
        0
      ),
      openDecisionCount: core.projects.reduce(
        (sum, project) => sum + project.openDecisionCount,
        0
      ),
      needsUserDecisionCount: core.projects.reduce(
        (sum, project) => sum + project.needsUserDecisionCount,
        0
      ),
      blockingRiskCount: core.projects.reduce(
        (sum, project) => sum + project.blockingRiskCount,
        0
      ),
      anomalyCount: core.projects.reduce(
        (sum, project) => sum + project.anomalyCount,
        0
      ),
      readyCount: core.projects.filter((project) => project.exportReadiness === "ready")
        .length,
      blockedCount: core.projects.filter(
        (project) => project.exportReadiness === "blocked"
      ).length,
      attentionCount: core.projects.filter((project) => attentionScore(project) > 0)
        .length,
      controllerCount: core.projects.reduce(
        (sum, project) => sum + project.controllerCount,
        0
      ),
      deviceCount: core.projects.reduce((sum, project) => sum + project.deviceCount, 0),
      infraCount: core.projects.reduce((sum, project) => sum + project.infraCount, 0),
      bomRowCount: core.projects.reduce((sum, project) => sum + project.bomRowCount, 0),
      bomUnitCount: core.projects.reduce(
        (sum, project) =>
          sum +
          (project.bomUnitCount ??
            project.supplySourceRollup.titan +
              project.supplySourceRollup.fabricated +
              project.supplySourceRollup.thirdParty +
              project.supplySourceRollup.inHouse),
        0
      )
    },
    dataGaps:
      core.projects.length === 0
        ? [
            "No saved projects are available yet.",
            "Create your first controls project to upload documents, review inputs, and run Specify."
          ]
        : [
            "Carrington includes a saved detailed workspace.",
            "The other benchmark projects are reference summaries only; choose New project to create a specification from your own inputs."
          ]
  };
};
